import { z } from 'zod';

import {
  InstagramCreatorAgeBucket,
  InstagramCreatorCountry,
  InstagramCreatorGender,
  InstagramCreatorInterest,
} from '@frontend/gql/social/graphql';
import { QueryVariables } from '../../types';
import {
  INSTAGRAM_SEARCH_PARAMS,
  InstagramCreatorAgeBucketSchema,
  InstagramCreatorCountrySchema,
  InstagramCreatorGenderSchema,
  InstagramCreatorInterestSchema,
} from '../types';

interface ParseInstagramSearchParamsOptions {
  searchParams: URLSearchParams;
  searchQuery: string;
  defaultVariables?: QueryVariables;
}

export const parseInstagramSearchParams = ({
  searchParams,
  searchQuery,
  defaultVariables,
}: ParseInstagramSearchParamsOptions): QueryVariables => {
  // Validate and parse age buckets - filter out invalid values
  const ageBucketsRaw =
    searchParams.get(INSTAGRAM_SEARCH_PARAMS.AGE_BUCKETS)?.split(',').filter(Boolean) ||
    defaultVariables?.ageBuckets ||
    [];
  const ageBuckets = ageBucketsRaw.filter(
    (value) => InstagramCreatorAgeBucketSchema.safeParse(value).success,
  ) as InstagramCreatorAgeBucket[];

  // Validate and parse genders (including 'ALL' option) - filter out invalid values
  const gendersRaw =
    searchParams.get(INSTAGRAM_SEARCH_PARAMS.GENDERS)?.split(',').filter(Boolean) || defaultVariables?.genders || [];
  const genderSchema = z.union([InstagramCreatorGenderSchema, z.literal('ALL')]);
  const genders = gendersRaw.filter((value) => genderSchema.safeParse(value).success) as (
    | InstagramCreatorGender
    | 'ALL'
  )[];

  // Validate and parse countries - filter out invalid values
  const countriesRaw =
    searchParams.get(INSTAGRAM_SEARCH_PARAMS.COUNTRIES)?.split(',').filter(Boolean) ||
    defaultVariables?.countries ||
    [];
  const countries = countriesRaw.filter(
    (value) => InstagramCreatorCountrySchema.safeParse(value).success,
  ) as InstagramCreatorCountry[];

  // Validate and parse interests - filter out invalid values
  const interestsRaw =
    searchParams.get(INSTAGRAM_SEARCH_PARAMS.INTERESTS)?.split(',').filter(Boolean) ||
    defaultVariables?.interests ||
    [];
  const interests = interestsRaw.filter(
    (value) => InstagramCreatorInterestSchema.safeParse(value).success,
  ) as InstagramCreatorInterest[];

  // Validate and parse numeric values with coercion
  const minFollowersParam = searchParams.get(INSTAGRAM_SEARCH_PARAMS.MIN_FOLLOWERS);
  const minFollowersResult = minFollowersParam ? z.coerce.number().safeParse(minFollowersParam) : null;
  const minFollowers = minFollowersResult?.success ? minFollowersResult.data : defaultVariables?.minFollowers;

  const maxFollowersParam = searchParams.get(INSTAGRAM_SEARCH_PARAMS.MAX_FOLLOWERS);
  const maxFollowersResult = maxFollowersParam ? z.coerce.number().safeParse(maxFollowersParam) : null;
  const maxFollowers = maxFollowersResult?.success ? maxFollowersResult.data : defaultVariables?.maxFollowers;

  const minEngagementsParam = searchParams.get(INSTAGRAM_SEARCH_PARAMS.MIN_ENGAGEMENTS);
  const minEngagementsResult = minEngagementsParam ? z.coerce.number().safeParse(minEngagementsParam) : null;
  const minEngagements = minEngagementsResult?.success ? minEngagementsResult.data : defaultVariables?.minEngagements;

  const maxEngagementsParam = searchParams.get(INSTAGRAM_SEARCH_PARAMS.MAX_ENGAGEMENTS);
  const maxEngagementsResult = maxEngagementsParam ? z.coerce.number().safeParse(maxEngagementsParam) : null;
  const maxEngagements = maxEngagementsResult?.success ? maxEngagementsResult.data : defaultVariables?.maxEngagements;

  // Validate and parse major audience age buckets - filter out invalid values
  const majorAudienceAgeBucketsRaw =
    searchParams.get(INSTAGRAM_SEARCH_PARAMS.MAJOR_AUDIENCE_AGE_BUCKETS)?.split(',').filter(Boolean) ||
    defaultVariables?.majorAudienceAgeBuckets ||
    [];
  const majorAudienceAgeBuckets = majorAudienceAgeBucketsRaw.filter(
    (value) => InstagramCreatorAgeBucketSchema.safeParse(value).success,
  ) as InstagramCreatorAgeBucket[];

  // Validate and parse major audience genders (including 'ALL' option) - filter out invalid values
  const majorAudienceGendersRaw = searchParams
    .get(INSTAGRAM_SEARCH_PARAMS.MAJOR_AUDIENCE_GENDERS)
    ?.split(',')
    .filter(Boolean) ||
    defaultVariables?.majorAudienceGenders || ['ALL'];
  const majorAudienceGenders = majorAudienceGendersRaw.filter((value) => genderSchema.safeParse(value).success) as (
    | InstagramCreatorGender
    | 'ALL'
  )[];

  // Validate and parse major audience countries - filter out invalid values
  const majorAudienceCountriesRaw =
    searchParams.get(INSTAGRAM_SEARCH_PARAMS.MAJOR_AUDIENCE_COUNTRIES)?.split(',').filter(Boolean) ||
    defaultVariables?.majorAudienceCountries ||
    [];
  const majorAudienceCountries = majorAudienceCountriesRaw.filter(
    (value) => InstagramCreatorCountrySchema.safeParse(value).success,
  ) as InstagramCreatorCountry[];

  // Parse string values (no validation needed)
  const before = searchParams.get(INSTAGRAM_SEARCH_PARAMS.BEFORE) || defaultVariables?.before || null;
  const after = searchParams.get(INSTAGRAM_SEARCH_PARAMS.AFTER) || defaultVariables?.after || null;

  // Validate and parse limit with Zod - must be positive number between 1 and 100
  const limitParam = searchParams.get(INSTAGRAM_SEARCH_PARAMS.LIMIT);
  const limitResult = limitParam ? z.coerce.number().min(1).max(100).safeParse(limitParam) : null;
  const limit = limitResult?.success ? limitResult.data : defaultVariables?.limit;

  // Parse boolean value for creator marketplace requirement
  const requireInCreatorMarketplaceParam = searchParams.get(INSTAGRAM_SEARCH_PARAMS.REQUIRE_IN_CREATOR_MARKETPLACE);
  const requireInCreatorMarketplaceResult = requireInCreatorMarketplaceParam ? z.coerce.boolean().safeParse(requireInCreatorMarketplaceParam) : null;
  const requireInCreatorMarketplace = requireInCreatorMarketplaceResult?.success && requireInCreatorMarketplaceResult.data === true
    ? true
    : defaultVariables?.requireInCreatorMarketplace;

  return {
    query: searchQuery,
    ageBuckets,
    genders,
    countries,
    interests,
    minFollowers,
    maxFollowers,
    minEngagements,
    maxEngagements,
    majorAudienceAgeBuckets,
    majorAudienceGenders,
    majorAudienceCountries,
    before,
    after,
    limit,
    requireInCreatorMarketplace,
  };
};
